import React, { useCallback, useEffect, useState } from 'react';
import {
  Power, PowerOff, Play, Loader2, AlertTriangle, CheckCircle2, RefreshCw, DollarSign,
} from 'lucide-react';
import { adminGet, adminPost, adminPatch } from '../data/api';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };
const card = { background: '#18181B', border: '1px solid #27272A' };

// Map endpoint → POST trigger URL (existing manual endpoints in ai_router.py).
// `null` means the endpoint is not user-triggerable (managed elsewhere).
const TRIGGER_URLS = {
  system_review: '/tt/admin/ai-system-review',
  pattern_detective: '/tt/admin/ai-pattern-detective',
  model_calibrator: '/tt/admin/ai-model-calibrator',
  edge_finder: '/tt/admin/ai-edge-finder',
  match_analysis: null,
  match_analysis_retry: null,
  screenshot_ocr: null,
};

const ENDPOINT_LABEL = {
  system_review: 'System Review',
  pattern_detective: 'Pattern Detective',
  model_calibrator: 'Model Calibrator',
  edge_finder: 'Edge Finder',
  match_analysis: 'Match Analysis (pre-match)',
  match_analysis_retry: 'Match Analysis Retry',
  screenshot_ocr: 'Screenshot OCR',
};

const ENDPOINT_DESC = {
  system_review: 'Analisi periodica della salute del sistema (verdetto + 3 raccomandazioni).',
  pattern_detective: 'Cerca pattern nascosti nei pick settled degli ultimi 7 giorni.',
  model_calibrator: 'Verifica accuratezza calibrazione modello su 14 giorni.',
  edge_finder: 'Cerca edge nascosti in match scheduled (max 10 per run).',
  match_analysis: 'Analisi pre-match (auto, gating EV ≥ 20% + Profit Strategy + cap 30/giorno).',
  match_analysis_retry: 'Retry interno se validator dice HIGH/CRITICAL.',
  screenshot_ocr: 'OCR caricato dall\'utente quando carica uno screenshot di quote.',
};

function fmtUsd(v) {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toFixed(4)}`;
}

function ConfirmDialog({ open, endpoint, estCost, onConfirm, onCancel, busy }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      data-testid="llm-confirm-dialog"
    >
      <div
        className="rounded-sm p-5 max-w-md w-full mx-4"
        style={card}
      >
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={16} className="text-amber-400" />
          <h3 className="text-sm font-semibold text-zinc-100" style={heading}>
            Confermi spesa LLM?
          </h3>
        </div>
        <div className="space-y-2 mb-4 text-xs text-zinc-400" style={mono}>
          <div>
            Endpoint: <span className="text-zinc-200">{ENDPOINT_LABEL[endpoint] || endpoint}</span>
          </div>
          <div>
            Costo stimato: <span className="text-amber-300">{fmtUsd(estCost)}</span>
          </div>
          <div className="text-[10px] text-zinc-600 mt-2">
            La stima usa il tracker corretto (chars/token=2.5 + safety 1.25). 
            Margine reale: ±10%.
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            data-testid="llm-confirm-cancel"
            className="px-3 py-1.5 rounded-sm text-xs text-zinc-400 hover:text-zinc-200 disabled:opacity-50"
            style={{ border: '1px solid #27272A' }}
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="llm-confirm-run"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium disabled:opacity-50"
            style={{ background: '#10B981', color: '#000' }}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            Esegui ora
          </button>
        </div>
      </div>
    </div>
  );
}

function EndpointCard({ entry, onToggle, onRun, runStatus }) {
  const url = TRIGGER_URLS[entry.endpoint];
  const isPreMatch = entry.is_pre_match;
  const isUserOnly = entry.is_user_initiated_only;
  const canTrigger = !!url;
  const status = runStatus?.[entry.endpoint] || {};

  return (
    <div
      className="rounded-sm p-3"
      style={card}
      data-testid={`llm-endpoint-card-${entry.endpoint}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-sm font-semibold text-zinc-100" style={heading}>
            {ENDPOINT_LABEL[entry.endpoint] || entry.endpoint}
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5" style={mono}>
            {ENDPOINT_DESC[entry.endpoint] || ''}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-sm"
            style={{
              ...mono,
              color: entry.auto_enabled ? '#86EFAC' : '#71717A',
              background: entry.auto_enabled ? '#0F2719' : '#1F1F22',
            }}
            data-testid={`llm-auto-status-${entry.endpoint}`}
          >
            {entry.auto_enabled ? 'AUTO ON' : 'AUTO OFF'}
          </span>
          <span className="text-[10px] text-amber-300" style={mono}>
            ~{fmtUsd(entry.est_cost_usd)}/run
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3">
        {!isPreMatch && !isUserOnly && (
          <button
            onClick={() => onToggle(entry.endpoint, !entry.auto_enabled)}
            data-testid={`llm-toggle-${entry.endpoint}`}
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] text-zinc-400 hover:text-zinc-200"
            style={{ border: '1px solid #27272A' }}
          >
            {entry.auto_enabled ? <PowerOff size={10} /> : <Power size={10} />}
            {entry.auto_enabled ? 'Disattiva auto' : 'Attiva auto'}
          </button>
        )}
        {canTrigger && (
          <button
            onClick={() => onRun(entry.endpoint, entry.est_cost_usd)}
            disabled={status.running}
            data-testid={`llm-run-${entry.endpoint}`}
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-medium disabled:opacity-50"
            style={{ background: '#10B981', color: '#000' }}
          >
            {status.running ? <Loader2 size={10} className="animate-spin" /> : <Play size={10} />}
            Esegui ora
          </button>
        )}
        {isPreMatch && (
          <span className="text-[10px] text-zinc-500" style={mono}>
            (auto, cap 30/giorno)
          </span>
        )}
        {isUserOnly && (
          <span className="text-[10px] text-zinc-500" style={mono}>
            (caricamento screenshot)
          </span>
        )}
        {status.message && (
          <span
            className={`text-[10px] ml-auto flex items-center gap-1 ${status.error ? 'text-rose-400' : 'text-emerald-400'}`}
            style={mono}
          >
            {status.error ? <AlertTriangle size={10} /> : <CheckCircle2 size={10} />}
            {status.message}
          </span>
        )}
      </div>
    </div>
  );
}

export default function LLMAutoRunPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmEp, setConfirmEp] = useState(null);
  const [confirmCost, setConfirmCost] = useState(0);
  const [runStatus, setRunStatus] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await adminGet('/tt/admin/ai-auto-run/settings');
      setData(r);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onToggle = async (endpoint, enabled) => {
    try {
      await adminPatch('/tt/admin/ai-auto-run/settings', undefined, { endpoint, enabled });
      await load();
    } catch (e) {
      setErr(String(e?.message || e));
    }
  };

  const onRun = (endpoint, estCost) => {
    setConfirmEp(endpoint);
    setConfirmCost(estCost);
  };

  const doRun = async () => {
    const ep = confirmEp;
    const url = TRIGGER_URLS[ep];
    if (!url) {
      setConfirmEp(null);
      return;
    }
    setRunStatus((s) => ({ ...s, [ep]: { running: true } }));
    setConfirmEp(null);
    try {
      const r = await adminPost(url);
      const status = r?.status || 'started';
      setRunStatus((s) => ({
        ...s,
        [ep]: { running: false, message: status === 'already_running' ? 'già in esecuzione' : 'avviato' },
      }));
    } catch (e) {
      setRunStatus((s) => ({
        ...s,
        [ep]: { running: false, error: true, message: String(e?.message || e).slice(0, 40) },
      }));
    }
    setTimeout(() => setRunStatus((s) => ({ ...s, [ep]: {} })), 6000);
  };

  const endpoints = data?.endpoints || [];

  return (
    <div className="space-y-3" data-testid="llm-auto-run-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarSign size={14} className="text-amber-400" />
          <h2 className="text-sm font-semibold text-zinc-100" style={heading}>
            LLM Auto-Run Gate (manual confirmation)
          </h2>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="llm-auto-run-refresh"
          className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] text-zinc-400 hover:text-zinc-200 disabled:opacity-50"
          style={{ border: '1px solid #27272A' }}
        >
          {loading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
          Refresh
        </button>
      </div>

      <div
        className="rounded-sm p-2.5 text-[10px] text-zinc-500"
        style={{ ...mono, ...card }}
        data-testid="llm-auto-run-note"
      >
        {data?.note || 'Pre-match LLM è auto. Tutti gli altri endpoint richiedono trigger manuale.'}
      </div>

      {err && (
        <div
          className="rounded-sm p-2 text-xs text-rose-300 flex items-center gap-2"
          style={{ ...mono, background: '#450A0A', border: '1px solid #7F1D1D' }}
          data-testid="llm-auto-run-error"
        >
          <AlertTriangle size={12} /> {err}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {endpoints.map((entry) => (
          <EndpointCard
            key={entry.endpoint}
            entry={entry}
            onToggle={onToggle}
            onRun={onRun}
            runStatus={runStatus}
          />
        ))}
      </div>

      <ConfirmDialog
        open={!!confirmEp}
        endpoint={confirmEp}
        estCost={confirmCost}
        onConfirm={doRun}
        onCancel={() => setConfirmEp(null)}
        busy={false}
      />
    </div>
  );
}
